import { z } from 'zod';
import {
	ECampaignPromotedType,
	ECampaignScheduleType,
	ICampaignBudgetBreakdown,
	ICampaignGoal,
	ICampaignPromotion,
	ICampaignSchedule,
} from './campaign';
import { IOption, OptionSchema } from '../misc';
import { validateWebsiteUrl } from 'src/lib/utils';
import { IIndustry } from 'src/services/industries';

interface ICampaignBudget {
	currency?: string;
	total: string;
	breakdown?: Array<ICampaignBudgetBreakdown>;
}

interface ICampaignPromotedObject {
	type: ECampaignPromotedType;
	value: Array<IOption>;
	ids?: string[];
}

export interface ICampaignForm {
	group?: string;
	title: string;
	description: string;
	goal?: Array<ICampaignGoal>;
	goalsList?: Array<string>;
	destination?: string;
	customerProfiles: Array<string>;
	promotedObject: ICampaignPromotedObject;
	promotion?: Array<ICampaignPromotion>;
	channels: string[];
	budget?: ICampaignBudget;
	placements?: string[];
}

const CampaignGoal: Zod.ZodType<ICampaignGoal> = z.object({
	id: z.string().nonempty('Goals id is required'),
	target: z.union([z.string(), z.number()]).optional(),
});

const CampaignPromotedObject: Zod.ZodType<ICampaignPromotedObject> = z
	.object({
		type: z.nativeEnum(ECampaignPromotedType),
		value: z.array(OptionSchema).default([]).optional(),
	})
	.superRefine(({ type, value }, ctx) => {
		// Only validate if value is provided and not empty
		if (type !== ECampaignPromotedType.landingPage && value && value.length > 0) {
			// Additional validation can be added here if needed
		}
	}) as Zod.ZodType<ICampaignPromotedObject>;

const CampaignPromotion: Zod.ZodType<ICampaignPromotion> = z.object({
	code: z.string().nonempty('Code is required'),
	description: z.string().nonempty('Description is required'),
});

const CampaignBudget: Zod.ZodType<ICampaignBudget> = z.object({
	total: z
		.string({
			errorMap: () => ({
				message: 'You must enter a value that is 0 or greater',
			}),
		})
		.nonempty('You must enter a value that is 0 or greater'),
	breakdown: z
		.array(
			z.object({
				id: z.string().nonempty('Id is required'),
				type: z.string().nonempty('Type is required'),
				budget: z
					.number()
					.nonnegative('You must enter a value that is 0 or greater'),
			}),
		)
		.optional(),
});

export const CampaignFormSchema: Zod.ZodType<ICampaignForm> = z.object({
	title: z.string().optional(),
	description: z.string().nonempty('Description is required'),
	goal: z
		.array(CampaignGoal, {
			errorMap: () => ({
				message: 'Goals must be selected',
			}),
		})
		.min(0)
		.optional(),
	goalsList: z
		.array(z.string(), {
			errorMap: () => ({
				message: 'Goals must be selected',
			}),
		})
		.min(0)
		.optional(),
	promotedObject: CampaignPromotedObject,
	destination: z
		.string()
		.optional()
		.refine((url) => !url || validateWebsiteUrl(url).isValid, {
			message: 'Please enter a valid URL',
		}),
	audience: z
		.array(z.string(), {
			errorMap: () => ({
				message: 'At least one customer profile must be selected',
			}),
		})
		.optional(),
	promotion: z.array(CampaignPromotion).optional(),
	channels: z.array(z.string()).optional(),
	budget: CampaignBudget.optional(),
	placements: z
		.array(z.string())
		.min(1, { message: 'One of the options must be selected' })
		.optional(),
});

export const CampaignScheduleSchema: Zod.ZodType<ICampaignSchedule> = z
	.object({
		type: z.nativeEnum(ECampaignScheduleType),
		startDate: z.date({
			errorMap: () => ({ message: 'Start Date is required' }),
		}),
		endDate: z.date().optional(),
	})
	.refine(
		({ type, endDate }) => {
			const isValid =
				(type === ECampaignScheduleType['One-off'] && endDate) ||
				type === ECampaignScheduleType['Evergreen'];
			return isValid;
		},
		{
			message: 'End Date is required',
			path: ['endDate'],
		},
	)
	.refine(
		({ startDate, endDate, type }) => {
			if (type === ECampaignScheduleType['Evergreen']) return true;
			if (!startDate || !endDate) return false;
			const startDateOnly = new Date(startDate).getTime();
			const endDateOnly = new Date(endDate).getTime();
			return startDateOnly < endDateOnly;
		},
		{
			message: 'End Date should be greater than Start Date',
			path: ['endDate'],
		},
	)
	.refine(
		({ endDate }) => {
			if (!endDate) return true;
			const now = new Date().setHours(0, 0, 0, 0);
			const end = new Date(endDate).getTime();
			return end >= now;
		},
		{
			message: 'End Date cannot be in the past',
			path: ['endDate'],
		},
	);
